import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { environment } from '../../../environments/environment';

interface GalleryCategory {
  id: string;
  title: string;
  mainImage: string;
  thumbnail: string;
  icon: string;
  isActive: boolean;
}

@Component({
  selector: 'app-photo-gallery',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './photo-gallery.html',
  styleUrl: './photo-gallery.scss'
})
  export class PhotoGallery implements OnInit {
  s3BaseUrl = environment.s3BaseUrl;
    @Input() isOpen = false;

      constructor() {
    // Las categorías se inicializan después del constructor
    // Vamos a usar ngOnInit para la inicialización
  }

  ngOnInit() {
    // Inicializar con la primera categoría activa
    const activeCategory = this.categories.find(cat => cat.isActive) || this.categories[0];
    this.currentImage = activeCategory.mainImage;
    this.currentImageAlt = activeCategory.title;
  }

  // Estado simplificado (sin carousel)
  currentImage = `${environment.s3BaseUrl}/images/sections/gallery-section/suites.webp`;
  currentImageAlt = 'Habitaciones y Suites';

  // Categorías de la galería
  categories: GalleryCategory[] = [
    {
      id: 'suites',
      title: 'Habitaciones y Suites',
      mainImage: `${environment.s3BaseUrl}/images/sections/gallery-section/suites.webp`,
      thumbnail: `${environment.s3BaseUrl}/images/sections/gallery-section/suites.webp`,
      icon: `${environment.s3BaseUrl}/icons/features/photo_gallery/bedroom.png`,
      isActive: true
    },
    {
      id: 'bungalows',
      title: 'Bungalows privados',
      mainImage: `${environment.s3BaseUrl}/images/sections/gallery-section/bungalows.webp`,
      thumbnail: `${environment.s3BaseUrl}/images/sections/gallery-section/bungalows.webp`,
      icon: `${environment.s3BaseUrl}/icons/features/photo_gallery/bungalows.png`,
      isActive: false
    },
    {
      id: 'green-area',
      title: 'Áreas verdes',
      mainImage: `${environment.s3BaseUrl}/images/sections/gallery-section/green-area.webp`,
      thumbnail: `${environment.s3BaseUrl}/images/sections/gallery-section/green-area.webp`,
      icon: `${environment.s3BaseUrl}/icons/features/photo_gallery/tree.png`,
      isActive: false
    },
    {
      id: 'slide-zone',
      title: 'Área de toboganes',
      mainImage: `${environment.s3BaseUrl}/images/sections/gallery-section/slide-zone.webp`,
      thumbnail: `${environment.s3BaseUrl}/images/sections/gallery-section/slide-zone.webp`,
      icon: `${environment.s3BaseUrl}/icons/features/photo_gallery/slide.png`,
      isActive: false
    },
    {
      id: 'pool-area',
      title: 'Circuito de piscinas',
      mainImage: `${environment.s3BaseUrl}/images/sections/gallery-section/pool-area.webp`,
      thumbnail: `${environment.s3BaseUrl}/images/sections/gallery-section/pool-area.webp`,
      icon: `${environment.s3BaseUrl}/icons/features/photo_gallery/pool.png`,
      isActive: false
    },
    {
      id: 'beach',
      title: 'Playas artificiales',
      mainImage: `${environment.s3BaseUrl}/images/sections/gallery-section/beach.webp`,
      thumbnail: `${environment.s3BaseUrl}/images/sections/gallery-section/beach.webp`,
      icon: `${environment.s3BaseUrl}/icons/features/photo_gallery/beach.png`,
      isActive: false
    }
  ];

  // Obtener la categoría activa
  get activeCategory(): GalleryCategory {
    return this.categories.find(cat => cat.isActive) || this.categories[0];
  }

  // Obtener categorías no activas para thumbnails
  get inactiveCategories(): GalleryCategory[] {
    return this.categories.filter(cat => !cat.isActive);
  }

  // Obtener thumbnails para mostrar a la izquierda de la imagen
  get leftThumbnails(): GalleryCategory[] {
    const activeIndex = this.categories.findIndex(cat => cat.isActive);
    const inactive = this.inactiveCategories;
    
    // Fórmula: izquierda = N-1 elementos (donde N es la posición de la categoría activa)
    const leftCount = activeIndex; // activeIndex ya es 0-based, así que es perfecto
    return inactive.slice(0, leftCount);
  }

  // Obtener thumbnails para mostrar a la derecha de la imagen
  get rightThumbnails(): GalleryCategory[] {
    const activeIndex = this.categories.findIndex(cat => cat.isActive);
    const inactive = this.inactiveCategories;
    
    // Fórmula: derecha = 6-N elementos (donde N es la posición de la categoría activa)
    const leftCount = activeIndex; // activeIndex ya es 0-based, así que es perfecto
    const rightCount = 6 - (activeIndex + 1); // +1 porque activeIndex es 0-based
    return inactive.slice(leftCount, leftCount + rightCount);
  }

  // Calcular el ancho de la descripción basado en el número de thumbnails a la derecha
  get descriptionWidth(): string {
    const rightCount = this.rightThumbnails.length;
    const leftCount = this.leftThumbnails.length;
    
    // Si hay 3 o menos thumbnails en cualquier lado, reducir el ancho
    if (rightCount <= 3 || leftCount <= 3) {
      return '320px'; // Ancho reducido cuando hay pocos thumbnails
    }
    
    return '350px'; // Ancho normal
  }

  // Determinar si la descripción debe ir a la izquierda o derecha
  get descriptionPosition(): 'left' | 'right' {
    const rightCount = this.rightThumbnails.length;
    
    // Si hay 3 o más thumbnails a la derecha, descripción a la derecha
    // Si hay 2 o menos thumbnails a la derecha, descripción a la izquierda
    return rightCount >= 3 ? 'right' : 'left';
  }

  // Cambiar categoría activa (simplificado, sin carousel)
  selectCategory(categoryId: string): void {
    // Encontrar la categoría seleccionada
    const selectedCategory = this.categories.find(cat => cat.id === categoryId);
    
    if (selectedCategory && !selectedCategory.isActive) {
      // Cambiar categoría activa
      this.categories.forEach(cat => cat.isActive = false);
      selectedCategory.isActive = true;
      
      // Actualizar imagen actual inmediatamente
      this.currentImage = selectedCategory.mainImage;
      this.currentImageAlt = selectedCategory.title;
    }
  }

  // Calcular posición dinámica de la descripción
  getDescriptionPosition(): string {
    const activeIndex = this.categories.findIndex(cat => cat.isActive);
    
    // Detectar el tamaño de pantalla para usar dimensiones correctas
    const isTabletLarge = window.innerWidth <= 1024 && window.innerWidth > 768;
    const isTabletSmall = window.innerWidth <= 768 && window.innerWidth > 576;
    const isMobile = window.innerWidth <= 576;
    
    // Dimensiones según el breakpoint
    let imageWidth: number;
    let expandedWidth: number;
    let gap: number;
    
    if (isTabletLarge) {
      // Tablet grande: proporciones reducidas
      imageWidth = 60;
      expandedWidth = 350;
      gap = 12;
    } else if (isTabletSmall || isMobile) {
      // Tablet pequeña y móvil: layout vertical, no necesita posicionamiento
      return '0px';
    } else {
      // Desktop: dimensiones originales
      imageWidth = 100;
      expandedWidth = 774;
      gap = 16;
    }
    
    const totalImages = this.categories.length;
    
    // Calcular cuántas imágenes hay a la derecha de la expandida
    const imagesOnRight = totalImages - activeIndex - 1;
    
    let leftPosition = 0;
    
    if (imagesOnRight >= 3) {
      // Si hay 3+ imágenes a la derecha, descripción a la derecha
      leftPosition = (activeIndex * imageWidth) + (activeIndex * gap) + expandedWidth;
      
      // Agregar espacio extra en tablet grande
      if (isTabletLarge) {
        leftPosition += 40; // 40px extra de espacio a la derecha
      }
    } else {
      // Si hay menos de 3 imágenes a la derecha, descripción a la izquierda
      const targetImageIndex = activeIndex - 2;
      if (targetImageIndex >= 0) {
        leftPosition = (targetImageIndex * imageWidth) + (targetImageIndex * gap) - 25;
        
        // Agregar espacio extra en tablet grande
        if (isTabletLarge) {
          leftPosition -= 40; // 40px extra de espacio a la izquierda
        }
      } else {
        leftPosition = 0;
      }
    }
    
    return `${leftPosition}px`;
  }

  // Calcular el transform dinámico para alejar la descripción según el tamaño de pantalla
  getDescriptionTransform(): string {
    const isTabletLarge = window.innerWidth <= 1024 && window.innerWidth > 768;
    const isTabletSmall = window.innerWidth <= 768 && window.innerWidth > 576;
    const isMobile = window.innerWidth <= 576;
    
    // Solo aplicar transform en tablet grande y desktop
    if (isTabletSmall || isMobile) {
      return 'translateX(0)';
    }
    
    // Calcular cuánto alejar la descripción según el tamaño de pantalla
    const screenWidth = window.innerWidth;
    let offsetX = 0;
    
    if (isTabletLarge) {
      // Tablet grande: offset base de 20px
      offsetX = 20;
    } else {
      // Desktop: offset progresivo según el ancho de pantalla
      // Mínimo 20px en 1025px, máximo 85px en 1400px+
      const minOffset = 20;
      const maxOffset = 85;
      const minWidth = 1025;
      const maxWidth = 1400;
      
      if (screenWidth <= minWidth) {
        offsetX = minOffset;
      } else if (screenWidth >= maxWidth) {
        offsetX = maxOffset;
      } else {
        // Interpolación lineal entre minOffset y maxOffset
        const ratio = (screenWidth - minWidth) / (maxWidth - minWidth);
        offsetX = minOffset + (ratio * (maxOffset - minOffset));
      }
    }
    
    const descriptionPos = this.descriptionPosition;
    
    if (descriptionPos === 'left') {
      // Si está a la izquierda, mover hacia la izquierda (valores negativos)
      return `translateX(-${offsetX}px)`;
    } else {
      // Si está a la derecha, mover hacia la derecha (valores positivos)
      return `translateX(${offsetX}px)`;
    }
  }

  getImageUrl(path: string): string {
    return `${this.s3BaseUrl}/${path}`;
  }
}
